import { useEffect, useState } from 'react';
import validate from 'validate.js';

import { constraints, getFirstErrorMessage, removeErrorKey } from '../utils/formValidation';
import type { postingConstraints } from '../utils/formValidation';

const useForm = (
  initial: { [key: string]: unknown } = {},
  constraintsObj: Partial<typeof postingConstraints | typeof constraints> = constraints,
) => {
  const [errorObj, setErrorObj] = useState({});
  const [hasErrors, setHasErrors] = useState({});
  const [inputs, setInputs] = useState(initial);
  const initialValues = Object.values(initial).join('');

  useEffect(() => {
    setInputs(initial);
  }, [initialValues]);

  useEffect(() => {
    const response = Object.values(errorObj).some((value) => value !== null);

    setHasErrors(response);
  }, [errorObj]);

  const clearFieldError = (name: string) => {
    // clear error for the field
    setErrorObj({
      ...errorObj,
      [name]: null,
    });
  };

  const addErrorKey = (name: string, message: string) => {
    setErrorObj({
      ...errorObj,
      [name]: message,
    });
  };

  const clearErrorObj = () => {
    setErrorObj({});
  };

  const hasFieldError = (name: string, value: unknown) => {
    const constraintsForField = {
      [name]: constraintsObj[name as keyof typeof constraintsObj],
    };

    const { presence: { allowEmpty = true } = {} } = constraintsForField[name] || {};

    /**
     * not able to allow null and undefined as valid values when validation key numericality has options
     * bypass entire validation check when allowEmpty is true
     */
    const bypassValidation = (value === '' || value === null || !value === undefined) && allowEmpty;

    const response = bypassValidation === false && validate({ [name]: value }, constraintsForField);

    if (response !== undefined && response.hasOwnProperty(name) && response[name].length > 0) {
      const message = removeErrorKey(name, getFirstErrorMessage(response[name]));

      setErrorObj({
        ...errorObj,
        [name]: message,
      });

      return true;
    }

    return false;
  };

  const handleChange = ({ target: { name, files, type, value } }: Record<string, Record<string, unknown>>) => {
    clearFieldError(name as string);

    if (type === 'file') {
      // @ts-expect-error
      // destructure 1st value from the files array
      [value] = files;
    }

    setInputs({
      ...inputs,
      [name as string]: value,
    });
  };

  function resetForm() {
    setInputs(initial);
  }

  function clearForm() {
    const blankState = Object.fromEntries(Object.entries(inputs).map(([key]) => [key, '']));

    setInputs(blankState);
  }

  function updateForm(newForm = initial) {
    setInputs({ ...newForm });
  }

  return {
    addErrorKey,
    clearErrorObj,
    clearFieldError,
    clearForm,
    errorObj,
    handleChange,
    hasErrors,
    hasFieldError,
    inputs,
    resetForm,
    updateForm,
  };
};

export default useForm;
